import React from "react";
import { Formik, FieldArray } from "formik";
import { CForm, CRow, CCol, CFormLabel, CButton } from "@coreui/react";
import CustomInput from "@/components/CustomInput";
import { emailTemplate } from "@/constants/translation/emailTemplate.translation";
import { useTranslationContext } from "@/context/TranslationContext";
import { useNavigate } from "react-router-dom";
import { masters } from "@/libs/routes";
import CustomEditor from "@/components/CKEditor";
import constants from "@/libs/constant";

/**
 * EmailTemplateForm Component
 * @param {Object} props - Component props
 * @param {Object} props.initialValues - Initial form values
 * @param {Function} props.doAddEmailTemplate - Function to handle form submission
 * @param {boolean} props.emailTemplatedLoaded - Loading state for email template
 * @param {Object} [props.validationSchema] - Form validation schema
 * @returns {JSX.Element} React component for email template form
 */
const EmailTemplateForm = ({ initialValues, onSubmit, emailTemplatedLoaded, validationSchema = {} }) => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  /**
   * Redirects to the email template list page.
   */
  const handleRedirectList = () => {
    navigate(masters.emailTemplate.list);
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => {
        
        
        onSubmit(values);
        setSubmitting(false);
      }}
      enableReinitialize
      validationSchema={validationSchema}
    >
      {({
        values,
        handleSubmit,
        handleChange,
        handleBlur,
        errors,
        touched,
        setFieldValue,
        isValid
      }) => (
        <CForm onSubmit={(e) => {
          
          
          e.preventDefault();
          handleSubmit(e);
        }}>
          <CRow className="mb-2 m-auto p-3 border rounded">
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(emailTemplate.form.slug_label)}
                type={constants.inputTypes.text}
                name={constants.formFields.emailTemplate.slug}
                placeholder={t(emailTemplate.form.slug_placeholder)}
                value={values.slug}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={initialValues?.id}
                error={touched.slug && errors.slug}
              />
            </CCol>
            <CCol lg={6} md={6} sm={12}>
              <CustomInput
                label={t(emailTemplate.form.parameter_label)}
                type={constants.inputTypes.text}
                name={constants.formFields.emailTemplate.parameter}
                placeholder={t(emailTemplate.form.parameter_label_placeholder)}
                value={values.parameter}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.parameter && errors.parameter}
                disabled={true}
              />
            </CCol>
          </CRow>
          <FieldArray name="email_templates">
            {() =>
              values.email_templates.map((lang, index) => (
                <CRow key={index} className="mb-2 m-auto p-3 border rounded">
                  <h5>{lang.language}</h5>
                  <CCol lg={12}>
                    <CustomInput
                      label={t(emailTemplate.form.title_label)}
                      type={constants.inputTypes.text}
                      name={`${constants.formFields.emailTemplate.emailTemplates}.${index}.${constants.formFields.emailTemplate.title}`}
                      placeholder={t(emailTemplate.form.title_placeholder)}
                      value={values.email_templates[index].title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email_templates?.[index]?.title && errors.email_templates?.[index]?.title}
                    />
                  </CCol>
                  <CCol lg={12}>
                    <CustomInput
                      label={t(emailTemplate.form.subject_label)}
                      type={constants.inputTypes.text}
                      name={`${constants.formFields.emailTemplate.emailTemplates}.${index}.${constants.formFields.emailTemplate.subject}`}
                      placeholder={t(emailTemplate.form.subject_placeholder)}
                      value={values.email_templates[index].subject}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email_templates?.[index]?.subject && errors.email_templates?.[index]?.subject}
                    />
                  </CCol>
                  <CCol lg={12}>
                    <CFormLabel>
                      {t(emailTemplate.form.content_label)}
                    </CFormLabel>
                    <CustomEditor
                      name={`${constants.formFields.emailTemplate.emailTemplates}.${index}.${constants.formFields.emailTemplate.content}`}
                      onChange={(htmlContent) => {
                        setFieldValue(`${constants.formFields.emailTemplate.emailTemplates}.${index}.${constants.formFields.emailTemplate.content}`, htmlContent);
                        let paramStr = htmlContent;
                        paramStr = paramStr.split(/{{(.*?)}}/);
                        paramStr = paramStr.filter((a, i) => i % 2 === 1);
                        let uniqueParams = [...new Set(paramStr)].join(", ");
                        setFieldValue(
                          constants.formFields.emailTemplate.parameter,
                          uniqueParams.replace(/<p>(&nbsp;|\s)*<\/p>/g, "")
                        );
                      }}
                    />
                  </CCol>
                </CRow>
              ))
            }
          </FieldArray>
          <CRow className="text-center mt-3">
          <CCol lg={12} md={12} sm={12} className="d-flex gap-2 justify-content-center">
              <CButton 
                color="primary" 
                className="px-4 mt-3" 
                type={constants.inputTypes.submit} 
                disabled={emailTemplatedLoaded}
                onClick={(e) => {
                }}
              >
                {t(emailTemplatedLoaded ? emailTemplate.form.loading_btn_text : emailTemplate.form.submit_btn_text)}
              </CButton>
              <CButton color="secondary" className="px-4 mt-3" type="button" disabled={emailTemplatedLoaded} onClick={handleRedirectList}>
                {t(emailTemplate.form.cancel_btn_text)}
              </CButton>
            </CCol>
          </CRow>

        </CForm>
      )}
    </Formik>
  );
};

export default EmailTemplateForm;
